import { useEffect, useState } from "react";
import "./class-publisher.css";
import { instanceMob } from "../../../utils/utils";
import InlineModal from "../../info-modal/infoModal";
import AlertBox from "../../alert-box/alertBox";

const days = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

const ClassPublisher = () => {
  const [tableData, setTableData] = useState([]);
  const [selectedDays, setSelectedDays] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);

  const openModal = (config) => {
    setModalConfig(config);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const getPublicationTimeLabel = (idx, h, m) => {
    const minNotice = h * 60 + m;

    let totalMinutes = 0 - minNotice;

    let publicationDayIdx = idx;
    if (totalMinutes < 0) {
      totalMinutes += 1440;
      publicationDayIdx = (idx - 1 + 7) % 7;
    }

    const pubHour = Math.floor(totalMinutes / 60);
    const pubMin = totalMinutes % 60;

    const ampm = pubHour >= 12 ? "p.m." : "a.m.";
    const hour12 = pubHour % 12 === 0 ? 12 : pubHour % 12;
    const formattedTime = `${hour12}:${pubMin
      .toString()
      .padStart(2, "0")} ${ampm}`;

    return `${days[publicationDayIdx]}, ${formattedTime}`;
  };

  const fetchPublishedTimes = () => {
    instanceMob
      .get("/admin/availability/published-times")
      .then((res) => res.data)
      .then((data) => {
        const result = data.day_settings;

        const parsedData = days.map((day, idx) => {
          const dayData = result?.[idx] || {};
          const min = dayData.min_notice || 0;
          const h = Math.floor(min / 60);
          const m = min % 60;

          return {
            day,
            h,
            m,
            anticipation: formatAnticipation(min),
            hour: getPublicationTimeLabel(idx, h, m),
            originalMinNotice: min,
          };
        });

        setTableData(parsedData);

        const selected = {};
        Object.entries(result).forEach(([idx, val]) => {
          selected[idx] = val.active;
        });

        setSelectedDays(selected);
      })
      .catch((err) => console.error("Error fetching published times:", err));
  };

  const getModifiedDays = () => {
    const modified = [];

    Object.entries(selectedDays).forEach(([idx, active]) => {
      if (!active) return;

      const current = tableData[idx];
      const totalMinutes = current.h * 60 + current.m;

      const originalMinutes =
        current.originalMinNotice !== undefined
          ? current.originalMinNotice
          : totalMinutes;

      if (originalMinutes !== totalMinutes) {
        modified.push({
          idx,
          day: days[idx],
          currentHour: getPublicationTimeLabel(
            idx,
            Math.floor(originalMinutes / 60),
            originalMinutes % 60
          ),
          newHour: current.hour,
        });
      }
    });

    return modified;
  };

  const formatAnticipation = (min) => {
    const hours = Math.floor(min / 60);
    const minutes = min % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m antes`;
    if (hours > 0) return `${hours} hora${hours > 1 ? "s" : ""} antes`;
    if (minutes > 0) return `${minutes} minuto${minutes > 1 ? "s" : ""} antes`;
    return "Sin anticipación";
  };

  const handleCheckboxChange = (idx) => {
    setSelectedDays((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleInputChange = (idx, field, value) => {
    const newData = [...tableData];
    newData[idx][field] = parseInt(value) || 0;
    const totalMin = newData[idx].h * 60 + newData[idx].m;
    newData[idx].anticipation = formatAnticipation(totalMin);
    newData[idx].hour = getPublicationTimeLabel(
      idx,
      newData[idx].h,
      newData[idx].m
    );
    setTableData(newData);
  };

  const handleSave = () => {
    const selected = Object.entries(selectedDays).filter(([_, v]) => v);
    if (selected.length === 0) return;

    const hasInvalidTime = selected.some(([idx]) => {
      const item = tableData[idx];
      return item.h === 0 && item.m === 0;
    });

    if (hasInvalidTime) {
      return openModal({
        title: "Selecciona una hora",
        description:
          "Debes elegir una hora válida para actualizar la publicación de clases. Este campo no puede quedar vacío.",
        primaryAction: {
          label: "Reintentar",
          onClick: () => closeModal(),
        },
      });
    }

    const modifiedDays = getModifiedDays();
    if (modifiedDays.length > 0) {
      return openModal({
        title: "¿Quieres reemplazar la hora de publicación?",
        description: (
          <>
            <p>
              Ya existe una hora de publicación configurada para los siguientes
              días:
            </p>
            <ul className="class-publisher-modal-list">
              {modifiedDays.map(({ day, currentHour, newHour }) => (
                <li key={day}>
                  <strong>{day}:</strong> {currentHour} → {newHour}
                </li>
              ))}
            </ul>
            <p>
              Si continúas, estas horas serán reemplazadas por las nuevas que
              seleccionaste.
            </p>
          </>
        ),
        primaryAction: {
          label: "Confirmar cambio",
          onClick: () => {
            closeModal();
            sendPublishedTimes();
          },
        },
        secondaryAction: {
          label: "Cancelar",
          onClick: () => closeModal(),
        },
      });
    }

    return openModal({
      title: "¿Deseas continuar?",
      description:
        "Esta configuración no cambia el día ni la hora de la clase, solo modifica el momento en que se mostrarán como disponibles para reservar desde la app.\n\nTodas las clases del día que seleccionaste se publicarán en el mismo horario",
      primaryAction: {
        label: "Confirmar",
        onClick: () => {
          closeModal();
          sendPublishedTimes();
        },
      },
      secondaryAction: {
        label: "Cancelar",
        onClick: () => closeModal(),
      },
    });
  };

  const sendPublishedTimes = () => {
    const payload = { day_settings: {} };

    tableData.forEach((item, idx) => {
      const active = selectedDays[idx] || false;
      const min_notice = item.h * 60 + item.m;

      payload.day_settings[idx] = {
        active,
        min_notice,
      };
    });

    instanceMob
      .post("/admin/availability/published-times", payload)
      .then(() => {
        fetchPublishedTimes();
        openModal({
          title: "Hora de publicación actualizada",
          description:
            "Las clases se publicarán a la hora seleccionada para todos los usuarios.\nEste cambio se aplicará a las clases programadas para el día seleccionado.",
          secondaryAction: {
            label: "Cerrar",
            onClick: () => {
              closeModal();
              closeModalViaButton();
            },
          },
        });
      })
      .catch(() => {
        openModal({
          title: "No pudimos actualizar la hora",
          description:
            "Ocurrió un problema al intentar cambiar la hora de publicación.\nPor favor, verifica tu conexión o intenta nuevamente más tarde.",
          primaryAction: {
            label: "Reintentar",
            onClick: () => {
              closeModal();
              sendPublishedTimes();
            },
          },
          secondaryAction: {
            label: "Cancelar",
            onClick: () => closeModal(),
          },
        });
      });
  };

  const toggleAllCheckboxes = () => {
    const allSelected = Object.values(selectedDays).every((v) => v);
    const newSelection = {};
    days.forEach((_, idx) => {
      newSelection[idx] = !allSelected;
    });
    setSelectedDays(newSelection);
  };

  const closeModalViaButton = () => {
    const cancelBtn = document.querySelector(
      "#classPublisherModal .class-publisher-modal-button-cancel"
    );

    if (cancelBtn) {
      cancelBtn.click();
    }
  };

  useEffect(() => {
    const checkbox = document.querySelector(
      "#classPublisherModal thead input[type='checkbox']"
    );
    if (!checkbox) return;
    const values = Object.values(selectedDays);
    checkbox.indeterminate = values.some((v) => v) && !values.every((v) => v);
  }, [selectedDays]);

  useEffect(() => {
    const modalElement = document.getElementById("classPublisherModal");

    const handleModalHidden = () => {
      setTableData([]);
      setSelectedDays({});
      setModalConfig(null);
      setShowModal(false);
      fetchPublishedTimes();
    };

    if (modalElement) {
      modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPublishedTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="class-publisher-container d-flex flex-column justify-content-center gap-2 text-center h-100">
        <h5 className="class-publisher-title fw-semibold text-primary-emphasis m-0">
          Configuración de publicación automática de clases
        </h5>
        <p className="class-publisher-description text-secondary m-0">
          Establece a qué hora deseas que se publiquen automáticamente las
          clases y servicios ya programados para cada día.
        </p>
        <button
          className="class-publisher-button-open btn btn-primary rounded-pill px-4 py-2 btn-sm"
          data-bs-toggle="modal"
          data-bs-target="#classPublisherModal"
        >
          Cambiar horarios
        </button>
      </div>

      <div
        className="modal fade"
        id="classPublisherModal"
        tabIndex={-1}
        aria-labelledby="classPublisherModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-5">
            <div className="class-publisher-modal-title">
              Controla el momento exacto en que tus clases se hacen visibles
              para tus usuarios
            </div>
            <div className="class-publisher-modal-description">
              Por defecto, todas las clases se publican automáticamente los
              domingos a las 12:00 a.m., pero entendemos que esto puede no
              ajustarse a la dinámica de todos los centros deportivos.
            </div>
            <div className="mt-2">
              <p className="class-publisher-modal-list-title mb-2">
                Con esta función puedes:
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-2 class-publisher-modal-description">
                  <i className="bi bi-check-circle-fill me-2 mt-1"></i>
                  Definir la hora exacta en que se habilitarán las reservas para
                  tus usuarios
                </li>
                <li className="d-flex align-items-start class-publisher-modal-description">
                  <i className="bi bi-check-circle-fill me-2 mt-1"></i>
                  Configurar con cuántas horas de anticipación deseas que el día
                  se publique
                </li>
                <li className="d-flex align-items-start class-publisher-modal-description">
                  <i className="bi bi-check-circle-fill me-2 mt-1"></i>
                  Los días que configures comienzan a contar desde las 12:00
                  a.m., por lo tanto, cualquier hora de publicación se aplicará
                  desde el inicio de ese día.
                </li>
              </ul>
            </div>

            <AlertBox
              title="Ejemplo:"
              description="Si programas las clases del lunes, y estableces el horario de publicación a las 9:00 p.m. del día anterior (domingo), los usuarios podrán reservar todas las clases desde ese momento del día lunes. Esto te permite controlar mejor la distribución de los cupos y evitar reservas inmediatas por parte de usuarios que se adelantan al resto."
            />

            <div className="class-publisher-modal-table table-responsive mt-4">
              <table className="table table-sm align-middle text-center">
                <thead className="table-light">
                  <tr>
                    <th className="text-center">
                      <div className="">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          onChange={toggleAllCheckboxes}
                          checked={Object.values(selectedDays).every((v) => v)}
                          indeterminate={
                            Object.values(selectedDays).some((v) => v) &&
                            !Object.values(selectedDays).every((v) => v)
                          }
                        />
                      </div>
                    </th>
                    <th className="text-start">Día</th>
                    <th className="text-start">Hora de publicación</th>
                    <th className="text-start">Tiempo de anticipación</th>
                    <th className="text-center border-0">H</th>
                    <th className="text-center border-0 p-0"></th>
                    <th className="text-center border-0">M</th>
                  </tr>
                </thead>

                <tbody>
                  {tableData.map(({ day, hour, anticipation, h, m }, idx) => (
                    <tr
                      key={idx}
                      className={selectedDays[idx] ? "row-selected" : ""}
                    >
                      <td className="text-center">
                        <div className="">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedDays[idx] || false}
                            onChange={() => handleCheckboxChange(idx)}
                          />
                        </div>
                      </td>
                      <td className="text-start">{day}</td>
                      <td className="text-start">{hour}</td>
                      <td className="text-start">{anticipation}</td>
                      <td className="class-publisher-modal-table-time text-center border-0 ">
                        <input
                          type="number"
                          className="class-publisher-modal-table-input text-center p-1"
                          value={h}
                          min={0}
                          onChange={(e) =>
                            handleInputChange(idx, "h", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center border-0 p-0 no-hover">:</td>
                      <td className="class-publisher-modal-table-time text-center border-0 ">
                        <input
                          type="number"
                          className="class-publisher-modal-table-input  text-center p-1"
                          value={m}
                          min={0}
                          onChange={(e) =>
                            handleInputChange(idx, "m", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-center mt-4 gap-4">
              <button
                data-bs-dismiss="modal"
                className="class-publisher-modal-button-cancel btn btn-outline-primary"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="class-publisher-modal-button-primary btn btn-primary"
              >
                Guardar configuración
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <InlineModal
          title={modalConfig.title}
          description={modalConfig.description}
          primaryAction={modalConfig.primaryAction}
          secondaryAction={modalConfig.secondaryAction}
        />
      )}
    </>
  );
};

export default ClassPublisher;
